
import { Loader2 } from 'lucide-react';
import { Checkbox, Search, Tooltip } from 'monday-ui-react-core';
import React, { useMemo, useState } from 'react';
import { isSupportedColumn } from '../../Config';
import { isNoSubitemsBoard, NO_SUBITEMS_BOARD_HINT } from '../../lib/subitemsBoard';
import AnchoredPopover from './AnchoredPopover';

export default function AddColumnMenu({
  anchorEl,
  boardColumns,
  loading, // board still being read — create mode opens before it arrives
  error, // board could not be read; `code` NO_SUBITEMS_COLUMN = the board has no subitems board yet
  usedIds,
  caption = 'Columns on this subitems board', // the board the columns come from
  onAdd,
  onRemove,
  onClose,
}) {
  const [query, setQuery] = useState('');

  const { supported, unsupported } = useMemo(() => {
    const q = query.trim().toLowerCase();
    // NB: not filtered by usedIds any more — a column already in the template has
    // to stay on the list, otherwise it could never be unticked from here.
    const visible = boardColumns.filter(
      (c) => !q || c.title.toLowerCase().includes(q) || c.type.toLowerCase().includes(q),
    );
    return {
      supported: visible.filter((c) => isSupportedColumn(c.type)),
      unsupported: visible.filter((c) => !isSupportedColumn(c.type)),
    };
  }, [boardColumns, query]);

  const isOn = (id) => usedIds.has(String(id));

  const toggle = (col) => (isOn(col.id) ? onRemove(col) : onAdd([col]));

  // Bulk actions act on the currently VISIBLE (filtered) supported rows only, so
  // "select all" after a search does what it looks like it does.
  const missing = supported.filter((c) => !isOn(c.id));
  const present = supported.filter((c) => isOn(c.id));

  // one call, so the editor inserts them in board order rather than one by one
  const selectAll = () => missing.length && onAdd(missing);
  const clearAll = () => present.forEach((c) => onRemove(c));

  // After every hook: the popover can open before the board has been read, and
  // re-renders into the column list as soon as it arrives.
  if (loading || error) {
    return (
      <AnchoredPopover anchorEl={anchorEl} onClose={onClose} width={300}>
        <div className="spt-menu-caption">{caption}</div>
        {loading ? (
          <div className="spt-menu-empty flex items-center gap-2" role="status">
            <Loader2 className="w-4 h-4 flex-none animate-spin" aria-hidden="true" />
            Reading your board columns and column settings
          </div>
        ) : (
          // A board with no subitems yet is not a failed read: say what is
          // missing and how to get it, instead of asking for a refresh that
          // changes nothing.
          <div className="spt-menu-empty" role="alert">
            {isNoSubitemsBoard(error) ? NO_SUBITEMS_BOARD_HINT : 'Could not read these columns. Please refresh and try again.'}
          </div>
        )}
      </AnchoredPopover>
    );
  }

  return (
    <AnchoredPopover anchorEl={anchorEl} onClose={onClose} width={300}>
      <div className="spt-menu-caption">{caption}</div>
      <div className="spt-menu-hint">
        Add or remove the columns on the template here
      </div>
      <Search
        size={Search.sizes.SMALL}
        placeholder="Search columns"
        value={query}
        onChange={setQuery}
        autoFocus
      />
      <div className="spt-menu-list">
        {supported.map((c) => (
          <div key={c.id} className="spt-menu-row spt-menu-row-check">
            <Checkbox checked={isOn(c.id)} onChange={() => toggle(c)} label={c.title} />
            <span className="spt-menu-row-type">{c.type}</span>
          </div>
        ))}
        {supported.length === 0 && (
          <div className="spt-menu-empty">
            {query ? 'No columns match' : 'This board has no columns that can be templated'}
          </div>
        )}
        {unsupported.length > 0 && (
          <>
            <div className="spt-menu-caption spt-menu-group">Not supported</div>
            {unsupported.map((c) => (
              <Tooltip key={c.id} content="This column type cannot be templated by the monday API">
                <div className="spt-menu-row spt-menu-row-disabled">
                  <span className="spt-menu-row-title">{c.title}</span>
                  <span className="spt-menu-row-type">{c.type}</span>
                </div>
              </Tooltip>
            ))}
          </>
        )}
      </div>
      {supported.length > 0 && (
        // Both actions are always shown rather than one toggling button: with the
        // ticks applying live, "Clear all" REMOVES columns from the template, and
        // that is too destructive to hide behind a label that changes under you.
        <div className="spt-menu-foot">
          <button
            type="button"
            className="spt-menu-linkbtn"
            disabled={missing.length === 0}
            onClick={selectAll}
          >
            Select all{missing.length ? ` (${missing.length})` : ''}
          </button>
          <button
            type="button"
            className="spt-menu-linkbtn"
            disabled={present.length === 0}
            onClick={clearAll}
          >
            Clear all{present.length ? ` (${present.length})` : ''}
          </button>
        </div>
      )}
    </AnchoredPopover>
  );
}
